/** biome-ignore-all lint/complexity/noStaticOnlyClass: 123 */
/** biome-ignore-all lint/suspicious/noExplicitAny: 123 */

import resolveModule from "@app/resolveModule/frontend";
import { CLIENT_CLOSED_STATUS_CODE } from "@core/Api/consts";
import type { Event } from "@core/Event/EventEmitter";
import isRefusal from "@core-ui/ApiServices/isRefusal";
import trimRoutePrefix from "@core-ui/ApiServices/trimRoutePrefix";
import { events } from "@core-ui/hooks/useApi";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import t from "@ext/localization/locale/translate";
import type FetchResponse from "./Types/FetchResponse";
import Method from "./Types/Method";
import MimeTypes from "./Types/MimeTypes";
import type Url from "./Types/Url";

export type OnDidCommandEv = { command: string; args: object; result: unknown };
export type OnWillCommandEv = { command: string; args: object };
export type OnSettledCommandEv = { command: string };

export type FetchServiceEvents = Event<"on-did-command", OnDidCommandEv> &
	Event<"on-will-command", OnWillCommandEv> &
	Event<"on-settled-command", OnSettledCommandEv>;

/**
 * @deprecated Consider using `useApi(..)` hook instead
 */
export default class FetchService {
	static fetch<T = any>(
		url: Url,
		body?: BodyInit,
		mime = MimeTypes.text,
		method = Method.POST,
		notifyError = true,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		_onDidCommand?: (command: string, args: object, result: unknown) => void,
		signal?: AbortSignal,
	): Promise<FetchResponse<T>> {
		return FetchService._fetch<T>(url, body, mime, method, () => notifyError, signal);
	}

	/**
	 * A read the user did not ask for (on page load, a preview, a count). If the server refuses it (401, 403), no
	 * dialog is shown; other failures are shown as usual.
	 */
	static fetchInBackground<T = any>(
		url: Url,
		body?: BodyInit,
		mime = MimeTypes.text,
		method = Method.POST,
		signal?: AbortSignal,
	): Promise<FetchResponse<T>> {
		return FetchService._fetch<T>(url, body, mime, method, (res) => !isRefusal(res.status), signal);
	}

	private static async _fetch<T>(
		url: Url,
		body: BodyInit,
		mime: MimeTypes,
		method: Method,
		shouldNotify: (res: FetchResponse<T>) => boolean,
		signal?: AbortSignal,
	): Promise<FetchResponse<T>> {
		const command = trimRoutePrefix(url);
		void events.emit("on-will-command", { command, args: {} });

		let res: FetchResponse<T>;
		try {
			res = await resolveModule("Fetcher")(
				url,
				body,
				mime,
				method,
				false,
				(command, args, result) => {
					void events.emit("on-did-command", { command, args, result });
				},
				signal,
			);
		} finally {
			void events.emit("on-settled-command", { command });
		}

		if (res.ok) return res;
		if (res.status === CLIENT_CLOSED_STATUS_CODE) return res;

		let error: any;
		if (res.status === 404) {
			error = new DefaultError(`${t("command")} "${command}" ${t("not-found2").toLowerCase()}`);
		} else {
			try {
				error = await res.json();
			} catch (e) {
				error = new DefaultError(
					t("app.error.command-failed.body"),
					null,
					{ html: true },
					false,
					t("app.error.command-failed.title"),
				);
				console.error(new Error(`${command} \n${e.message}`));
			}
		}
		if (shouldNotify(res)) ErrorConfirmService.notify(error);
		return res;
	}
}
